/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";

import { FONT_HIEU, MAU } from "assets/theme/avocadoBrand";

const KHOA_LUU_NHOM_DONG = "sidenav_nhom_dong";

function docNhomDongDaLuu() {
  try {
    return JSON.parse(localStorage.getItem(KHOA_LUU_NHOM_DONG)) ?? {};
  } catch {
    return {};
  }
}

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller;
  const location = useLocation();
  const collapseName = location.pathname.replace("/", "");

  // Mỗi "title" là 1 nhóm chức năng (Thu mua/Kho/Báo cáo/Quản lý...) — bấm
  // vào để đóng/mở, nhớ lại lựa chọn qua localStorage để không phải đóng lại
  // mỗi lần load trang.
  const [nhomDong, setNhomDong] = useState(docNhomDongDaLuu);

  function toggleNhom(key) {
    setNhomDong((cur) => {
      const moi = { ...cur, [key]: !cur[key] };
      localStorage.setItem(KHOA_LUU_NHOM_DONG, JSON.stringify(moi));
      return moi;
    });
  }

  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  let nhomHienTai = null;
  const renderRoutes = routes.map(({ type, name, icon, title, noCollapse, key, href, route }) => {
    let returnValue;

    if (type === "collapse") {
      if (nhomHienTai && nhomDong[nhomHienTai]) return null;

      returnValue = href ? (
        <Link
          href={href}
          key={key}
          target="_blank"
          rel="noreferrer"
          sx={{ textDecoration: "none" }}
        >
          <SidenavCollapse
            name={name}
            icon={icon}
            active={key === collapseName}
            noCollapse={noCollapse}
          />
        </Link>
      ) : (
        <NavLink key={key} to={route}>
          <SidenavCollapse name={name} icon={icon} active={key === collapseName} />
        </NavLink>
      );
    } else if (type === "title") {
      nhomHienTai = key;
      const dong = !!nhomDong[key];
      returnValue = (
        <MDBox
          key={key}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          pl={3}
          pr={2}
          mt={2}
          mb={1}
          ml={1}
          onClick={() => toggleNhom(key)}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography
            color={textColor}
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            sx={{
              letterSpacing: "0.1em",
              // Tên nhóm là nhãn phân vùng, phải lùi sau tên các mục —
              // dùng xanh ruột bơ thay vì trắng để bớt tranh chấp thị giác.
              ...(textColor === "white" && { color: MAU.flesh }),
            }}
          >
            {title}
          </MDTypography>
          <MDTypography
            color={textColor}
            variant="caption"
            display="flex"
            sx={{ ...(textColor === "white" && { color: MAU.flesh }) }}
          >
            <Icon fontSize="small">{dong ? "expand_more" : "expand_less"}</Icon>
          </MDTypography>
        </MDBox>
      );
    } else if (type === "divider") {
      returnValue = (
        <Divider
          key={key}
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
        />
      );
    }

    return returnValue;
  });

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <MDBox pt={3} pb={1} px={4} textAlign="center">
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox component={NavLink} to="/" display="flex" alignItems="center">
          {brand && <MDBox component="img" src={brand} alt="Brand" width="2rem" />}
          <MDBox
            width={!brandName && "100%"}
            sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
          >
            <MDTypography
              component="h6"
              variant="button"
              fontWeight="medium"
              color={textColor}
              sx={{
                // Tên thương hiệu đặt bằng Fraunces, giữ chữ thường —
                // variant="button" của template viết hoa toàn bộ, làm serif
                // mất hết phần nét lên/nét xuống vốn tạo nhận diện.
                fontFamily: FONT_HIEU,
                fontSize: "1.05rem",
                lineHeight: 1.2,
                letterSpacing: "-0.01em",
                textTransform: "none",
              }}
            >
              {brandName}
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
      <List>{renderRoutes}</List>
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
    "avocado",
  ]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
